import {z} from "zod/v4-mini"
import {registerTemplateGraphNode} from "#template/template-node/register"
import {GeometryAttributeNode, pointGeometryAttributeLikeMarker} from "#template/nodes/geometry-graph/attribute/attribute-node"
import {createPointGeometryAttributeData} from "#template/nodes/geometry-graph/interfaces"
import type {GeometryAttributeData, GeometryValueData, GeometryValueRefData} from "#template/nodes/geometry-graph/interfaces"
import type {ScalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import {scalarArg} from "#template/nodes/geometry-graph/attribute/scalar-arg"
import type {TemplateNodeDefinition} from "#template/template-node/types"
import {getTemplateNodeReturns} from "#template/template-node/validation"
import type {BuilderInlet} from "#template/runtime-graph/graph-builder"
import type {GraphBuilderScope} from "#template/runtime-graph/graph-builder-scope"
import type {NodeEvaluator} from "#template/node-evaluator"
import type {PointGeometryAttributeLikeNode} from "#template/nodes/geometry-graph/attribute/attribute-node"
import type {NonNullableParameters} from "#template/nodes/geometry-graph/common/geometry-graph-node"
import type {GeomBuilderContext, ValueRef} from "#template/geometry-processing/geometry-graph"
import {Primitives} from "#template/geometry-processing/geometry-graph"
import {applyGeometryOperator} from "#template/nodes/geometry-graph/common/geometry-graph-node-declaration"

export type PointGeometryLinePoints1Parameters = {
    x0: ScalarArg | null
    x1: ScalarArg | null
    numPoints: ScalarArg | null
}

const definition: TemplateNodeDefinition<PointGeometryLinePoints1Parameters> = {
    nodeClass: "PointGeometryLinePoints1",
    validation: {
        parameters: z.object({
            x0: z.nullable(scalarArg),
            x1: z.nullable(scalarArg),
            numPoints: z.nullable(scalarArg),
        }),
        returns: getTemplateNodeReturns(),
    },
}
const resolveScalarArg = (value: GeometryValueData | number): GeometryValueRefData | number => (typeof value === "number" ? value : value.ref)

@registerTemplateGraphNode
export class PointGeometryLinePoints1 extends GeometryAttributeNode<0, PointGeometryLinePoints1Parameters> implements PointGeometryAttributeLikeNode {
    get [pointGeometryAttributeLikeMarker]() {
        return true as const
    }

    public override get definition() {
        return definition
    }

    protected override hasValidParameters(
        parameters: PointGeometryLinePoints1Parameters,
    ): parameters is PointGeometryLinePoints1Parameters & NonNullableParameters<PointGeometryLinePoints1Parameters> {
        return parameters.x0 !== null && parameters.x1 !== null && parameters.numPoints !== null
    }

    protected override setupGeometryData(
        parameters: NonNullableParameters<PointGeometryLinePoints1Parameters>,
        scope: GraphBuilderScope,
        evaluator: NodeEvaluator,
        ctx: GeomBuilderContext,
    ): {evaluatedGeometryData: BuilderInlet<GeometryAttributeData<0> | null>; invalidator?: BuilderInlet<null>} {
        const {x0, x1, numPoints} = parameters

        const [x0Valid, x0Invalid] = scope.branch(evaluator.evaluate(x0))
        const [x1Valid, x1Invalid] = scope.branch(evaluator.evaluate(x1))
        const [numPointsValid, numPointsInvalid] = scope.branch(evaluator.evaluate(numPoints))

        const geometryAttributeData = scope.pureLambda(
            scope.tuple(x0Valid, x1Valid, numPointsValid, ctx),
            ([x0, x1, numPoints, ctx]) =>
                createPointGeometryAttributeData(
                    applyGeometryOperator(
                        (x0: ValueRef | number, x1: ValueRef | number, numPoints: ValueRef | number) => Primitives.linePoints1(ctx, x0, x1, numPoints),
                        resolveScalarArg(x0),
                        resolveScalarArg(x1),
                        resolveScalarArg(numPoints),
                    ),
                ),
            "pointGeometryLinePoints1",
        )

        return {evaluatedGeometryData: geometryAttributeData, invalidator: scope.phi(x0Invalid, x1Invalid, numPointsInvalid)}
    }
}
